import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquare, Calendar, MapPin, Clock, CheckCircle, XCircle } from 'lucide-react';
import ChatDialog from './ChatDialog';
import { useUnreadMessages } from '../hooks/useUnreadMessages';
import { getImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const { byRequest, refresh } = useUnreadMessages();

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/customer/requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
    } catch (error) {
      console.error('Error loading requests:', error);
      toast.error('Fehler beim Laden der Anfragen');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm('Möchtest du diese Anfrage wirklich stornieren?')) {
      return;
    }

    setCancellingId(requestId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/customer/requests/${requestId}/cancel`,
        { reason: 'Vom Kunden storniert' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Anfrage storniert');
      loadRequests();
    } catch (error) {
      console.error('Error cancelling request:', error);
      toast.error('Fehler beim Stornieren');
    } finally {
      setCancellingId(null);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      new: {
        label: 'Gesendet',
        color: 'bg-[#FFF0D1] text-[#2B2142]',
        icon: Clock
      },
      viewed: {
        label: 'Gesehen',
        color: 'bg-purple-100 text-purple-800',
        icon: CheckCircle
      },
      responded: {
        label: 'Antwort erhalten',
        color: 'bg-green-100 text-green-800',
        icon: MessageSquare
      },
      in_communication: {
        label: 'In Kommunikation',
        color: 'bg-yellow-100 text-yellow-800',
        icon: MessageSquare
      },
      completed: {
        label: 'Abgeschlossen',
        color: 'bg-gray-100 text-gray-800',
        icon: CheckCircle
      },
      cancelled: {
        label: 'Storniert',
        color: 'bg-red-100 text-red-800',
        icon: XCircle
      }
    };

    return configs[status] || configs.new;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // event_date ist eine freie String-Spalte (String(50)), nicht zwingend ISO
    const parsed = new Date(dateString);
    if (isNaN(parsed.getTime())) return dateString;
    return parsed.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-3xl shadow-xl p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="h-24 bg-gray-100 rounded"></div>
          <div className="h-24 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="bg-white rounded-3xl shadow-xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <MessageSquare className="w-6 h-6 text-[#2B2142]" />
          <h2 className="text-2xl font-bold text-gray-900">Gesendete Anfragen</h2>
        </div>
        
        <div className="text-center py-12">
          <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">Noch keine Anfragen gesendet.</p>
          <p className="text-sm text-gray-500 mt-2">
            Finde Dienstleister und sende deine erste Anfrage!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl shadow-xl p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <MessageSquare className="w-6 h-6 text-[#2B2142]" />
          <h2 className="text-2xl font-bold text-gray-900">Gesendete Anfragen</h2>
        </div>
        <span className="text-sm text-gray-500">{requests.length} Anfrage(n)</span>
      </div>

      <div className="space-y-4">
        {requests.map((request) => {
          const statusConfig = getStatusConfig(request.status);
          const StatusIcon = statusConfig.icon;

          const providerHref = `/provider/${request.provider_public_id || request.provider_id}`;

          return (
            <div key={request.id} className="border border-gray-200 rounded-2xl p-6 hover:shadow-md transition-shadow">
              {request.provider_company_name && (
                <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100">
                  {request.provider_picture ? (
                    <img
                      src={getImageUrl(request.provider_picture)}
                      alt={request.provider_company_name}
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#F7B693] flex items-center justify-center text-[#2B2142] font-bold flex-shrink-0">
                      {request.provider_company_name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500">Deine Anfrage an</p>
                    <Link
                      to={providerHref}
                      className="font-semibold text-[#2B2142] hover:underline truncate block"
                    >
                      {request.provider_company_name}
                    </Link>
                  </div>
                </div>
              )}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-gray-900 text-lg">{request.event_type}</h3>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${statusConfig.color}`}>
                      <StatusIcon className="w-3.5 h-3.5" />
                      {statusConfig.label}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-sm text-gray-600">
                    {request.event_date && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(request.event_date)}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{request.event_location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>Gesendet am {formatDate(request.created_at)}</span>
                    </div>
                  </div>
                </div>

                {request.status !== 'cancelled' && request.status !== 'completed' && (
                  <button
                    onClick={() => handleCancelRequest(request.id)}
                    disabled={cancellingId === request.id}
                    className="px-4 py-2 border-2 border-red-600 text-red-600 hover:bg-red-50 font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    <XCircle className="w-4 h-4" />
                    {cancellingId === request.id ? 'Wird storniert...' : 'Stornieren'}
                  </button>
                )}
              </div>

              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-semibold text-gray-700 mb-2">Deine Nachricht:</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{request.message}</p>
              </div>

              {request.last_message && (
                <div className="mt-4 bg-[#FFF0D1]/30 rounded-xl p-4">
                  <h4 className="font-semibold text-[#2B2142] mb-2">
                    {request.last_message_sender_role === 'customer' ? 'Deine letzte Nachricht:' : 'Letzte Nachricht:'}
                  </h4>
                  <p className="text-[#2B2142] text-sm leading-relaxed">{request.last_message}</p>
                  <p className="text-xs text-[#2B2142] mt-2">
                    {formatDate(request.last_message_at)}
                  </p>
                </div>
              )}

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setSelectedRequest(request)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-lg transition-colors"
                >
                  <MessageSquare className="w-4 h-4" />
                  Chat öffnen
                  {byRequest[request.id] > 0 && (
                    <span className="ml-1 min-w-[20px] h-5 px-1.5 rounded-full bg-[#F7B693] text-[#2B2142] text-xs font-bold flex items-center justify-center">
                      {byRequest[request.id] > 99 ? '99+' : byRequest[request.id]}
                    </span>
                  )}
                </button>
              </div>

              {request.status === 'cancelled' && request.cancellation_reason && (
                <div className="mt-4 bg-red-50 rounded-xl p-4">
                  <h4 className="font-semibold text-red-900 mb-2">Stornierungsgrund:</h4>
                  <p className="text-red-800 text-sm">{request.cancellation_reason}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ChatDialog
        request={selectedRequest}
        open={!!selectedRequest}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedRequest(null);
            loadRequests();
          }
        }}
        title={selectedRequest?.event_type || 'Anfrage'}
        subtitle={selectedRequest?.event_location}
        onActivity={refresh}
      />

      <div className="mt-6 p-4 bg-[#FFF0D1]/30 border border-[#2B2142]/20 rounded-xl">
        <p className="text-sm text-[#2B2142]">
          💡 <strong>Hinweis:</strong> Deine Anfragen findest du jederzeit hier.
          Sobald ein Dienstleister antwortet, siehst du die Antwort in dieser Übersicht.
        </p>
      </div>
    </div>
  );
};

export default CustomerRequests;
